import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Card, Stack, Typography } from "@altimateai/lego";
import {
  useDatabricksCostTotalEstimate,
  useDatabricksCostTrendsEstimate,
  useDatabricksFutureChartData,
  useSearchParam,
} from "../hooks";
import AccordionTitle from "../../../summary/components/AccordionTitle";
import FailureComponent from "../../../summary/components/FailureComponent";
import classes from "../../../summary/summary.module.scss";
import DatabricksCostCategorySelector from "./DatabricksCostCategorySelector";
import DatabricksCostChartSkeleton from "./DatabricksCostChartSkeleton";
import DatabricksCostTotalsTile from "./DatabricksCostTotalsTile";
import DatabricksCostTotalsTileSkeleton from "./DatabricksCostTotalsTileSkeleton";
import DatabricksFutureChart from "./DatabricksFutureChart";
import DatabricksCloudCostChart from "../../cloudCost/DatabricksCloudCostChart";
import { useCloudCostProjectionTotal } from "../../cloudCost/useCloudCost";
import { useAppState } from "@/modules/app/useAppContext";
import {
  asTrendsGroup,
  CLOUD_COSTS_GROUP,
  COST_CATEGORY_OPTIONS,
  GROUP_TO_CATEGORY,
  TRENDS_GROUPS,
} from "./costConstants";

interface Props {
  startDate: Date;
  endDate: Date | null;
}

const DatabricksCostFutureView = ({ startDate, endDate }: Props) => {
  const { showCloudCosts } = useAppState();
  const [selectedCategory, setSelectedCategory] = useSearchParam("futureCategory");
  const selectedGroup = asTrendsGroup(selectedCategory);
  // Cloud Costs lens gated behind `showCloudCosts`; off → DBU-only main view.
  const categoryOptions: readonly string[] = showCloudCosts ? COST_CATEGORY_OPTIONS : TRENDS_GROUPS;
  const isCloudCosts = showCloudCosts && selectedCategory === CLOUD_COSTS_GROUP;

  const startDateStr = dayjs(startDate).format("YYYY-MM-DD");
  const endDateStr = endDate ? dayjs(endDate).format("YYYY-MM-DD") : "";
  const enabled = Boolean(startDateStr && endDateStr);

  const {
    data: trendsData,
    isLoading: trendsLoading,
    error: trendsError,
  } = useDatabricksCostTrendsEstimate(
    {
      start_date: startDateStr,
      end_date: endDateStr,
      category: selectedGroup ? GROUP_TO_CATEGORY[selectedGroup] : "total",
    },
    enabled
  );

  // The totals tile always shows the 6 spec-group breakdown (category=total).
  const {
    data: totalCosts,
    isLoading: totalLoading,
    error: totalError,
  } = useDatabricksCostTotalEstimate({ start_date: startDateStr, end_date: endDateStr }, enabled);

  const { chartData, segments } = useDatabricksFutureChartData(trendsData);

  // Projected cloud cost (actual + estimate) as an extra totals tile, only when
  // there is cloud cost to show. Shares the projection chart's query.
  const { total: cloudProjectedTotal, isLoading: cloudProjLoading } = useCloudCostProjectionTotal(
    {},
    showCloudCosts && enabled
  );

  // Hide the category tabs until the first chart has rendered; afterwards keep
  // them visible across tab switches so only the canvas swaps.
  const [hasRenderedChart, setHasRenderedChart] = useState(false);
  useEffect(() => {
    if (!trendsLoading) setHasRenderedChart(true);
  }, [trendsLoading]);

  // The tile always reflects category=total, so each segment's `product` is
  // already a display-ready spec-group label — no formatting needed.
  const totalsBreakdown = useMemo(() => {
    const base = (totalCosts?.segment_breakdown ?? [])
      .filter(item => item.estimated_cost > 0)
      .map(item => ({ key: item.product, label: item.product, value: item.estimated_cost }));
    return showCloudCosts && cloudProjectedTotal > 0
      ? [...base, { key: CLOUD_COSTS_GROUP, label: CLOUD_COSTS_GROUP, value: cloudProjectedTotal }]
      : base;
  }, [totalCosts, showCloudCosts, cloudProjectedTotal]);

  // Only a failing totals query blocks the whole section; a failing DBU trends
  // query is handled inline in the chart area so the tabs stay accessible (and
  // the Cloud Costs lens, which fetches its own data, is never blocked by it).
  const hasError = totalError;

  return (
    <Card className={classes.section_card} id="tour-databricks-cost-future">
      <AccordionTitle
        title={<Typography variant="subheading">Projected Costs</Typography>}
        howToText="How to read the charts"
        howToContent={
          <Typography variant="body">
            These charts show projected Databricks costs for this year. Solid bars represent actual
            costs, lighter bars represent projected costs based on recent usage trends. Hover over
            bars to see detailed breakdowns.
          </Typography>
        }
      />
      <Stack>
        {hasError ? (
          <FailureComponent title="Failed to load cost projections. Please try again later." />
        ) : (
          <>
            {totalLoading || cloudProjLoading ? (
              <DatabricksCostTotalsTileSkeleton />
            ) : (
              <DatabricksCostTotalsTile
                title="Total Projected Costs"
                helpText={`Projected Databricks costs from ${startDateStr} to ${endDateStr}`}
                total={totalCosts?.total ?? 0}
                breakdown={totalsBreakdown}
              />
            )}
            <div className={classes.graphs}>
              {(hasRenderedChart || isCloudCosts) && (
                <Stack className="al-mb-4">
                  <DatabricksCostCategorySelector
                    options={categoryOptions}
                    selected={showCloudCosts ? selectedCategory : selectedGroup}
                    onSelect={setSelectedCategory}
                  />
                </Stack>
              )}
              {isCloudCosts ? (
                <DatabricksCloudCostChart
                  variant="future"
                  startDate={startDate}
                  endDate={endDate}
                />
              ) : trendsError ? (
                <FailureComponent title="Failed to load cost projections. Please try again later." />
              ) : trendsLoading ? (
                <DatabricksCostChartSkeleton />
              ) : (
                <div className="al-w-full al-h-[450px] al-p-4">
                  <DatabricksFutureChart
                    data={chartData}
                    segments={segments}
                    selectedGroup={selectedGroup}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default DatabricksCostFutureView;
